import type { ReactElement } from 'react'
import { MarketingLayout } from '../Marketing/MarketingLayout'
import styles from './PrivacyPolicy.module.css'

export function PrivacyPolicy(): ReactElement {
  return (
    <MarketingLayout>
      <div className={styles.maxWidth}>
        <header className={styles.header}>
          <h1 className={styles.title}>Privacy Policy</h1>
          <div className={styles.date}>Effective Date: December 14, 2025</div>
        </header>

        <p className={styles.paragraph}>
          aether studio (“Aether,” “we,” “us,” or “our”) respects your privacy and is committed to protecting it through this Privacy Policy. This policy describes how we collect, use, disclose, and safeguard your information when you use our website, applications, tools, APIs, and related services (collectively, the “Services”).
        </p>

        <p className={styles.paragraph}>
          By accessing or using the Services, you agree to the collection and use of information in accordance with this Privacy Policy.
        </p>

        <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>1. Scope</h2>
          <p className={styles.paragraph}>
            This Privacy Policy applies to all users of aether studio, including visitors, registered users, creators, collaborators, and enterprise customers. It covers information collected online and offline in connection with the Services.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>2. Information We Collect</h2>

          <h3 className={styles.subSectionTitle}>2.1 Information You Provide Directly</h3>
          <p className={styles.paragraph}>We may collect information that you voluntarily provide, including:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}><strong>Account Information:</strong> name, email address, username, password, organization name, role, and profile details.</li>
            <li className={styles.listItem}><strong>Billing Information:</strong> billing address, payment method details, transaction history (processed via third-party payment processors).</li>
            <li className={styles.listItem}><strong>Content and Assets:</strong> images, videos, prompts, edits, masks, annotations, metadata, creative graphs, and other content you upload, generate, or modify using the Services.</li>
            <li className={styles.listItem}><strong>Communications:</strong> messages, emails, support requests, feedback, survey responses, and other communications with us.</li>
          </ul>

          <h3 className={styles.subSectionTitle}>2.2 Information Collected Automatically</h3>
          <p className={styles.paragraph}>When you use the Services, we may automatically collect:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}><strong>Usage Data:</strong> feature usage, tool execution logs, credit usage, session duration, and interaction patterns.</li>
            <li className={styles.listItem}><strong>Device and Technical Data:</strong> IP address, browser type, device identifiers, operating system, language, and referring URLs.</li>
            <li className={styles.listItem}><strong>Log Data:</strong> timestamps, error logs, API calls, and performance metrics.</li>
          </ul>

          <h3 className={styles.subSectionTitle}>2.3 Information from Third Parties</h3>
          <p className={styles.paragraph}>We may receive information from:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Authentication Providers (e.g., OAuth providers).</li>
            <li className={styles.listItem}>Payment Processors for transaction verification.</li>
            <li className={styles.listItem}>Cloud and AI Service Providers involved in compute, storage, model inference, or training.</li>
            <li className={styles.listItem}>Enterprise or Team Accounts where an administrator manages access.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
          <p className={styles.paragraph}>We use your information to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Provide, operate, and maintain the Services.</li>
            <li className={styles.listItem}>Enable creative workflows, collaboration, versioning, and asset management.</li>
            <li className={styles.listItem}>Execute AI-powered tools, including image generation, editing, training, and inference.</li>
            <li className={styles.listItem}>Process payments, manage credits, and prevent fraud.</li>
            <li className={styles.listItem}>Improve performance, usability, reliability, and safety.</li>
            <li className={styles.listItem}>Personalize experiences and recommendations.</li>
            <li className={styles.listItem}>Communicate with you about updates, security alerts, and support matters.</li>
            <li className={styles.listItem}>Comply with legal obligations and enforce our terms.</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>4. AI Models, Training, and Generated Content</h2>
          
          <h3 className={styles.subSectionTitle}>4.1 User Content and AI Processing</h3>
          <p className={styles.paragraph}>
            Your uploaded or generated content may be processed by AI systems to provide the requested functionality (e.g., generation, editing, analysis). We do not use your private content to train shared or public AI models without your explicit permission.
          </p>

          <h3 className={styles.subSectionTitle}>4.2 Licensed Identities and Models</h3>
          <p className={styles.paragraph}>
            Where applicable, aether studio supports licensed identities, LoRAs, or models. Use of such assets is governed by applicable licensing terms and contractual restrictions.
          </p>

          <h3 className={styles.subSectionTitle}>4.3 Ownership</h3>
          <p className={styles.paragraph}>
            You retain ownership of your content. aether studio claims no ownership over your creative assets, subject to the limited licenses required to operate the Services.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>5. How We Share Information</h2>
          <p className={styles.paragraph}>
            We do not sell your personal information. We may share information only as follows:
          </p>
          <ul className={styles.list}>
            <li className={styles.listItem}><strong>Service Providers:</strong> cloud hosting, storage, analytics, AI inference/training providers, payment processors, and customer support tools.</li>
            <li className={styles.listItem}><strong>Team and Enterprise Sharing:</strong> content and usage data shared according to workspace permissions.</li>
            <li className={styles.listItem}><strong>Legal and Compliance:</strong> when required by law, regulation, or valid legal process.</li>
            <li className={styles.listItem}><strong>Business Transfers:</strong> in connection with a merger, acquisition, financing, or sale of assets.</li>
          </ul>
          <p className={styles.paragraph}>
            All service providers are contractually obligated to protect your data and use it only for authorized purposes.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>6. Data Retention</h2>
          <p className={styles.paragraph}>We retain personal information only for as long as necessary to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Provide the Services.</li>
            <li className={styles.listItem}>Comply with legal, accounting, or regulatory requirements.</li>
            <li className={styles.listItem}>Resolve disputes and enforce agreements.</li>
          </ul>
          <p className={styles.paragraph}>
            You may delete your account or content at any time, subject to technical and legal limitations.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>7. Security</h2>
          <p className={styles.paragraph}>
            We implement administrative, technical, and organizational safeguards designed to protect your information, including encryption, access controls, and secure infrastructure. However, no system is completely secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>8. Your Rights and Choices</h2>
          <p className={styles.paragraph}>Depending on your location, you may have rights to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Access, correct, or delete your personal information.</li>
            <li className={styles.listItem}>Object to or restrict processing.</li>
            <li className={styles.listItem}>Request data portability.</li>
            <li className={styles.listItem}>Withdraw consent where processing is based on consent.</li>
          </ul>
          <p className={styles.paragraph}>
            Requests can be made by contacting us at the address below.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>9. Cookies and Tracking Technologies</h2>
          <p className={styles.paragraph}>We use cookies and similar technologies to:</p>
          <ul className={styles.list}>
            <li className={styles.listItem}>Authenticate users.</li>
            <li className={styles.listItem}>Remember preferences.</li>
            <li className={styles.listItem}>Analyze usage and improve performance.</li>
          </ul>
          <p className={styles.paragraph}>
            You can control cookies through your browser settings, but some features may not function properly.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>10. International Data Transfers</h2>
          <p className={styles.paragraph}>
            aether studio operates globally. Your information may be processed and stored in countries other than your own. We implement appropriate safeguards for international transfers where required by law.
          </p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>11. Children’s Privacy</h2>
          <p className={styles.paragraph}>
            The Services are not intended for individuals under the age of 13.
          </p>
        </section>
      </div>
    </MarketingLayout>
  )
}
